/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, June 18th 2022, 12:42:59 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { Organization } from "lib/schemas";
import { converHexColorToRGBTriplets } from "lib/utils";
import { OSHexColorPicker, OSColorPickerSkeleton } from "ui-widgets/colors/ColorPickers";
import { HelperText } from "ui-widgets/typography/CustomTypography";
import { OSLoadingButton, OSAlert, OSPaperContainer } from "ui-widgets";
import { useAPIMutateOrganization, useAPIWorkerFetchOrganization } from "organizations/services/OrganizationsAPI";
import { RGBTriplets } from "theme/styles";

interface OrganizationBrandSettingsProp {
  organizationId: Organization["id"];
}

export const OrganizationBrandSettings = ({ organizationId }: OrganizationBrandSettingsProp) => {
  const {
    colorScheme,
    isSuccessfulOrganizationPatch,
    organization,
    onChangeBrandColor,
    saveColorScheme,
    isPatchingOrganization,
    isFetchingOrganization,
  } = useOrganizationBrandSettingsLogic(organizationId);

  return (
    <Stack direction="row" spacing={2}>
      <OSPaperContainer sx={{ width: "50%" }}>
        <Stack spacing={2}>
          <Typography variant="h5">Brand Colors</Typography>

          <HelperText>Customize how your brand appears on the portals that host your services.</HelperText>

          {isSuccessfulOrganizationPatch && (
            <OSAlert variant="outlined" severity="success">
              Successfully updated color scheme.
            </OSAlert>
          )}

          <Stack direction="row" spacing={5}>
            {organization && !isFetchingOrganization ? (
              <>
                <Stack>
                  <Typography variant="h5" className="os-content-description">
                    Primary Color
                  </Typography>
                  <OSHexColorPicker
                    initialColor={organization.color_scheme?.primary ?? "#7b00b1"}
                    onChangeColor={(primaryColor) => onChangeBrandColor({ primary: primaryColor })}
                  />
                </Stack>
                <Stack>
                  <Typography variant="h5" className="os-content-description">
                    Secondary Color
                  </Typography>
                  <OSHexColorPicker
                    initialColor={organization.color_scheme?.secondary ?? "#CC0099"}
                    onChangeColor={(secondaryColor) => onChangeBrandColor({ secondary: secondaryColor })}
                  />
                </Stack>
              </>
            ) : (
              <>
                <OSColorPickerSkeleton />
                <OSColorPickerSkeleton />
              </>
            )}
          </Stack>

          <OSLoadingButton
            onClick={saveColorScheme}
            variant="contained"
            size="large"
            isLoading={isPatchingOrganization}
          >
            Save
          </OSLoadingButton>
        </Stack>
      </OSPaperContainer>

      <OSPaperContainer sx={{ width: "50%" }}>
        <Typography variant="h5">Preview</Typography>

        {organization ? <OrganizationBrandPreview colorScheme={colorScheme} /> : <Skeleton height={300} />}
      </OSPaperContainer>
    </Stack>
  );
};

const useOrganizationBrandSettingsLogic = (organizationId: Organization["id"]) => {
  const [colorScheme, setColorScheme] = React.useState<Organization["color_scheme"]>();

  const { organization, isFetchingOrganization, fetchOrganization } = useAPIWorkerFetchOrganization();

  const {
    patchData,
    isMakingAPIRequest: isPatchingOrganization,
    isSuccessfulAPIRequest: isSuccessfulOrganizationPatch,
  } = useAPIMutateOrganization({
    filterByGlobalOrganization: false,
  });

  const onChangeBrandColor = (colors: Partial<Organization["color_scheme"]>) => {
    if (colors.primary) setColorScheme((prevColorScheme) => ({ ...prevColorScheme, primary: colors.primary }));
    if (colors.secondary) setColorScheme((prevColorScheme) => ({ ...prevColorScheme, secondary: colors.secondary }));
  };

  const saveColorScheme = () => {
    patchData({
      apiPathParams: [organizationId],
      body: JSON.stringify({ color_scheme: colorScheme }),
      setValidAPIResponse: (organization) => {},
    });
  };

  React.useEffect(() => {
    fetchOrganization(organizationId);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  React.useEffect(() => {
    if (!organization?.id) return;

    setColorScheme(organization.color_scheme);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  return {
    isSuccessfulOrganizationPatch,
    organization,
    onChangeBrandColor,
    colorScheme,
    saveColorScheme,
    isPatchingOrganization,
    isFetchingOrganization,
  };
};

const OrganizationBrandPreview = ({ colorScheme }: { colorScheme: Organization["color_scheme"] }) => {
  const { getPreviewPrimaryRGBColorString, getPreviewSecondaryRGBColorString } =
    useOrganizationBrandPreviewLogic(colorScheme);
  return (
    <div className="os-brand-color-preview">
      <div className="os-bc-head">
        <div className="os-bc-action os-bc-action1"></div>
        <div className="os-bc-action os-bc-action2"></div>
        <div className="os-bc-action os-bc-action3"></div>
        <div className="os-bc-action4"></div>
      </div>
      <div className="os-bc-header" style={{ background: getPreviewPrimaryRGBColorString(0.04) }}>
        <div className="os-bc-logo" style={{ background: getPreviewPrimaryRGBColorString(1) }}></div>
        <div className="os-bc-menu" style={{ background: getPreviewPrimaryRGBColorString(0.1) }}></div>
      </div>
      <div className="os-bc-main" style={{ background: getPreviewPrimaryRGBColorString(0.04) }}>
        {Array(3)
          .fill(0)
          .map((i) => (
            <div className="os-bc-service">
              <div className="os-bc-service-title" style={{ background: getPreviewPrimaryRGBColorString(0.6) }}></div>
              <div className="os-bc-service-cta" style={{ background: getPreviewSecondaryRGBColorString(1) }}></div>
            </div>
          ))}
      </div>
      <div className="os-bc-footer" style={{ background: getPreviewPrimaryRGBColorString(1) }}></div>
    </div>
  );
};

const useOrganizationBrandPreviewLogic = (colorScheme: Organization["color_scheme"]) => {
  const primaryRGBAsString = colorScheme?.primary
    ? converHexColorToRGBTriplets(colorScheme.primary).join(",")
    : RGBTriplets.primary;

  const secondaryRGBAsString = colorScheme?.secondary
    ? converHexColorToRGBTriplets(colorScheme.secondary).join(",")
    : RGBTriplets.secondary;

  return {
    getPreviewPrimaryRGBColorString: (alpha: number) => `rgba(${primaryRGBAsString}, ${alpha})`,
    getPreviewSecondaryRGBColorString: (alpha: number) => `rgba(${secondaryRGBAsString}, ${alpha})`,
  };
};
